import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from "react";

const OPTION_ROW_HEIGHT = 32;
const LIST_MAX_HEIGHT = 192;
const VIRTUALIZATION_OVERSCAN = 6;

const NoteTitleDropdown = ({
  entries = [],
  favorites = [],
  value = "",
  onChange,
  onToggleFavorite,
  isUpdatingFavorites = false,
  disabled = false,
  hasError = false,
  errorMessageId,
  openUpward = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [liveMessage, setLiveMessage] = useState("");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [viewMode, setViewMode] = useState("all");
  const [scrollTop, setScrollTop] = useState(0);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false);
  const resultsListId = useId();
  const noteTitleInfoTooltipId = useId();
  const rootRef = useRef(null);
  const resultsScrollRef = useRef(null);
  const searchInputRef = useRef(null);
  const previousViewModeRef = useRef(viewMode);
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const options = useMemo(() => {
    if (!Array.isArray(entries)) {
      return [];
    }

    return [...entries].sort((a, b) => {
      const aTitle = String(a?.title || "").trim();
      const bTitle = String(b?.title || "").trim();
      return aTitle.localeCompare(bTitle, undefined, { sensitivity: "base" });
    });
  }, [entries]);
  const favoriteValues = useMemo(() => {
    if (!Array.isArray(favorites)) {
      return [];
    }

    return Array.from(
      new Set(
        favorites
          .map((favorite) => (typeof favorite === "string" ? favorite.trim() : ""))
          .filter(Boolean)
      )
    );
  }, [favorites]);
  const favoriteSet = useMemo(() => new Set(favoriteValues), [favoriteValues]);
  const optionsForCurrentView = useMemo(() => {
    if (viewMode !== "favorites") {
      return options;
    }

    return options.filter((entry) => {
      const optionValue = entry?.ien || entry?.title;
      const favoriteLookupValue =
        typeof optionValue === "string" ? optionValue.trim() : String(optionValue || "").trim();
      return favoriteSet.has(favoriteLookupValue);
    });
  }, [favoriteSet, options, viewMode]);
  const availableFavoriteCount = useMemo(() => {
    return options.reduce((count, entry) => {
      const optionValue = entry?.ien || entry?.title;
      const favoriteLookupValue =
        typeof optionValue === "string" ? optionValue.trim() : String(optionValue || "").trim();
      return favoriteSet.has(favoriteLookupValue) ? count + 1 : count;
    }, 0);
  }, [favoriteSet, options]);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = deferredSearchTerm.trim().toLowerCase();
    if (!normalizedQuery) return optionsForCurrentView;
    return optionsForCurrentView.filter((entry) =>
      String(entry?.title || "")
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [deferredSearchTerm, optionsForCurrentView]);
  const totalRows = filteredOptions.length;
  const visibleRowCount = Math.ceil(LIST_MAX_HEIGHT / OPTION_ROW_HEIGHT) + VIRTUALIZATION_OVERSCAN * 2;
  const startIndex = Math.max(0, Math.floor(scrollTop / OPTION_ROW_HEIGHT) - VIRTUALIZATION_OVERSCAN);
  const endIndex = Math.min(totalRows, startIndex + visibleRowCount);
  const visibleOptions = filteredOptions.slice(startIndex, endIndex);
  const topSpacerHeight = startIndex * OPTION_ROW_HEIGHT;
  const bottomSpacerHeight = Math.max(0, (totalRows - endIndex) * OPTION_ROW_HEIGHT);
  const selectedTitle = useMemo(() => {
    const match = options.find((entry) => (entry?.ien || entry?.title) === value);
    return match?.title || value || "";
  }, [options, value]);
  const normalizedSelectedValue = useMemo(() => {
    return typeof value === "string" ? value.trim() : String(value || "").trim();
  }, [value]);
  const selectedOptionIndexInAll = useMemo(() => {
    if (!normalizedSelectedValue) return -1;
    return options.findIndex((entry, index) => {
      const optionValue = entry?.ien || entry?.title || `Note Title ${index + 1}`;
      const normalizedOptionValue =
        typeof optionValue === "string"
          ? optionValue.trim()
          : String(optionValue || "").trim();
      return normalizedOptionValue === normalizedSelectedValue;
    });
  }, [normalizedSelectedValue, options]);
  const selectedIsFavorited = useMemo(() => {
    if (!normalizedSelectedValue) return false;
    return favoriteSet.has(normalizedSelectedValue);
  }, [favoriteSet, normalizedSelectedValue]);
  const getOptionTitle = (entry, index) => entry?.title || `Note Title ${index + 1}`;
  const getOptionValue = (entry, index) => entry?.ien || getOptionTitle(entry, index);
  const isOptionSelected = (optionValue) => {
    const normalizedOptionValue =
      typeof optionValue === "string"
        ? optionValue.trim()
        : String(optionValue || "").trim();
    return normalizedOptionValue === normalizedSelectedValue;
  };

  const focusOptionAtIndex = (index) => {
    if (!filteredOptions.length) return;
    const nextIndex = Math.max(0, Math.min(index, filteredOptions.length - 1));
    const targetEntry = filteredOptions[nextIndex];
    const targetTitle = getOptionTitle(targetEntry, nextIndex);
    const targetValue = getOptionValue(targetEntry, nextIndex);
    const selectedAnnouncement = isOptionSelected(targetValue) ? ", selected" : "";
    const optionTop = nextIndex * OPTION_ROW_HEIGHT;
    const optionBottom = optionTop + OPTION_ROW_HEIGHT;

    if (resultsScrollRef.current) {
      const currentScrollTop = resultsScrollRef.current.scrollTop;
      const viewportBottom = currentScrollTop + LIST_MAX_HEIGHT;
      if (optionTop < currentScrollTop) {
        resultsScrollRef.current.scrollTop = optionTop;
      } else if (optionBottom > viewportBottom) {
        resultsScrollRef.current.scrollTop = optionBottom - LIST_MAX_HEIGHT;
      }
    }

    setActiveOptionIndex(nextIndex);
    setLiveMessage(
      `${targetTitle}${selectedAnnouncement}, option ${nextIndex + 1} of ${filteredOptions.length}`
    );

    requestAnimationFrame(() => {
      const optionButton = rootRef.current?.querySelector(
        `[data-option-index="${nextIndex}"]`
      );
      if (optionButton && typeof optionButton.focus === "function") {
        optionButton.focus();
      }
    });
  };

  const moveOptionFocus = (direction) => {
    if (!filteredOptions.length) return;
    if (activeOptionIndex >= 0) {
      focusOptionAtIndex(activeOptionIndex + direction);
      return;
    }
    if (direction > 0) {
      focusOptionAtIndex(0);
      return;
    }
    focusOptionAtIndex(filteredOptions.length - 1);
  };

  useEffect(() => {
    if (viewMode === "favorites" && favoriteValues.length === 0) {
      setViewMode("all");
    }
  }, [favoriteValues.length, viewMode]);

  useEffect(() => {
    if (!searchTerm) return;
    const noun = filteredOptions.length === 1 ? "note title" : "note titles";
    setLiveMessage(`${filteredOptions.length} ${noun} found`);
  }, [searchTerm, filteredOptions.length]);

  useEffect(() => {
    if (!selectedTitle) return;
    setLiveMessage(`Selected note title ${selectedTitle}`);
  }, [selectedTitle]);

  const getDefaultViewMode = () => (favoriteValues.length > 0 ? "favorites" : "all");

  const openPickerWithDefaultView = () => {
    setViewMode(getDefaultViewMode());
    setScrollTop(0);
    setActiveOptionIndex(-1);
    if (resultsScrollRef.current) {
      resultsScrollRef.current.scrollTop = 0;
    }
    setIsPickerOpen(true);
  };

  const closePicker = ({ clearSearch = false } = {}) => {
    setIsPickerOpen(false);
    setActiveOptionIndex(-1);
    if (clearSearch) {
      setSearchTerm("");
    }
  };

  const handleTriggerOpen = () => {
    if (!isPickerOpen) {
      openPickerWithDefaultView();
    }
  };

  useEffect(() => {
    if (!isPickerOpen) return;

    const handlePointerDownOutside = (event) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target)) return;
      closePicker({ clearSearch: true });
    };

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      closePicker({ clearSearch: true });
    };

    window.addEventListener("pointerdown", handlePointerDownOutside);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDownOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isPickerOpen]);

  useEffect(() => {
    setScrollTop(0);
    setActiveOptionIndex(-1);
    if (resultsScrollRef.current) {
      resultsScrollRef.current.scrollTop = 0;
    }
  }, [deferredSearchTerm]);

  useEffect(() => {
    const previousViewMode = previousViewModeRef.current;
    if (previousViewMode === viewMode) {
      return;
    }

    const shouldJumpToSelectedInAll =
      viewMode === "all" &&
      previousViewMode === "favorites" &&
      !deferredSearchTerm.trim() &&
      selectedOptionIndexInAll >= 0 &&
      !selectedIsFavorited;

    const nextScrollTop = shouldJumpToSelectedInAll
      ? selectedOptionIndexInAll * OPTION_ROW_HEIGHT
      : 0;

    setScrollTop(nextScrollTop);
    setActiveOptionIndex(-1);
    if (resultsScrollRef.current) {
      resultsScrollRef.current.scrollTop = nextScrollTop;
    }
    previousViewModeRef.current = viewMode;
  }, [
    deferredSearchTerm,
    selectedIsFavorited,
    selectedOptionIndexInAll,
    viewMode,
  ]);

  useEffect(() => {
    if (activeOptionIndex < filteredOptions.length) return;
    setActiveOptionIndex(filteredOptions.length - 1);
  }, [activeOptionIndex, filteredOptions.length]);

  const handleQuickSelect = (nextValue, nextTitle = "") => {
    if (!onChange) return;
    onChange({ target: { value: nextValue } });
    setLiveMessage(`Selected note title ${nextTitle || nextValue}`);
    setSearchTerm("");
    closePicker();
  };

  return (
    <div ref={rootRef} className="relative w-full max-w-[680px]">
      <div className="block text-sm font-semibold text-gray-800 ml-1.5 mb-1.5">
        <span className="inline-flex items-center gap-1.5">
          <span>Note Title</span>
          <button
            type="button"
            className="relative inline-flex h-4 w-4 items-center justify-center rounded-full text-knowtex-grey"
            aria-label="Note title information"
            aria-describedby={isInfoTooltipOpen ? noteTitleInfoTooltipId : undefined}
            onMouseEnter={() => setIsInfoTooltipOpen(true)}
            onMouseLeave={() => setIsInfoTooltipOpen(false)}
            onFocus={() => setIsInfoTooltipOpen(true)}
            onBlur={() => setIsInfoTooltipOpen(false)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsInfoTooltipOpen(false);
            }}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M8 7V11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <circle cx="8" cy="4.75" r="0.75" fill="currentColor" />
            </svg>
            {isInfoTooltipOpen ? (
              <span
                id={noteTitleInfoTooltipId}
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 w-64 -translate-x-1/2 rounded-md bg-gray-900 px-2 py-1 text-[11px] font-normal leading-4 text-white shadow-lg"
              >
                This title determines how the note appears in the EHR.
              </span>
            ) : null}
          </button>
          <span className="text-red-500">* (Required)</span>
        </span>
      </div>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>
      <div className="flex flex-row items-end gap-2 mb-2 w-full">
        <input
          ref={searchInputRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-label="Search note titles"
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorMessageId : undefined}
          aria-controls={resultsListId}
          aria-expanded={isPickerOpen}
          placeholder="Search..."
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            handleTriggerOpen();
          }}
          onFocus={handleTriggerOpen}
          onClick={handleTriggerOpen}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              if (!isPickerOpen) {
                openPickerWithDefaultView();
                requestAnimationFrame(() => moveOptionFocus(1));
                return;
              }
              moveOptionFocus(1);
              return;
            }

            if (event.key === "ArrowUp") {
              event.preventDefault();
              if (!isPickerOpen) {
                openPickerWithDefaultView();
                requestAnimationFrame(() => moveOptionFocus(-1));
                return;
              }
              moveOptionFocus(-1);
              return;
            }

            if (event.key === "Enter" && activeOptionIndex >= 0) {
              const activeEntry = filteredOptions[activeOptionIndex];
              if (!activeEntry) return;
              event.preventDefault();
              handleQuickSelect(
                getOptionValue(activeEntry, activeOptionIndex),
                getOptionTitle(activeEntry, activeOptionIndex)
              );
            }
          }}
          disabled={disabled}
          className="w-32 px-3 py-2 text-sm border border-gray-200 rounded-md bg-white text-gray-800 focus:outline-none focus:border-indigo-300 focus:ring-[3px] focus:ring-indigo-100 disabled:opacity-60"
        />
        <button
          type="button"
          aria-label={`Selected note title: ${selectedTitle || "No note title selected"}`}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorMessageId : undefined}
          aria-controls={resultsListId}
          aria-expanded={isPickerOpen}
          onMouseEnter={() =>
            setLiveMessage(`Current selected note title: ${selectedTitle || "No note title selected"}`)
          }
          onFocus={() =>
            setLiveMessage(`Current selected note title: ${selectedTitle || "No note title selected"}`)
          }
          onClick={() => {
            handleTriggerOpen();
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }}
          disabled={disabled}
          className={`flex-1 min-w-[240px] py-2 px-3 text-sm border rounded-md text-left ${
            hasError
              ? "border-red-500 bg-red-50 text-red-800"
              : "border-indigo-200 bg-indigo-50 text-accent1"
          }`}
          title={selectedTitle || "No note title selected"}
        >
          <span
            className={`block truncate ${selectedTitle ? "font-semibold" : "text-gray-500"}`}
          >
            {selectedTitle || "No note title selected"}
          </span>
        </button>
      </div>
      {isPickerOpen || searchTerm ? (
        <div
          id={`${resultsListId}-popover`}
          className={`absolute left-0 right-0 z-50 rounded-md border border-gray-200 bg-white shadow-lg ${
            openUpward ? "bottom-full mb-1 origin-bottom" : "top-full mt-1 origin-top"
          }`}
          style={{
            animation: `${openUpward ? "noteTitleDropdownOpenUp" : "noteTitleDropdownOpenDown"} 140ms ease-out`,
          }}
        >
          <style>{`
            @keyframes noteTitleDropdownOpenUp {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes noteTitleDropdownOpenDown {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <div
            ref={resultsScrollRef}
            id={resultsListId}
            role="listbox"
            className="max-h-48 overflow-auto"
            style={{ maxHeight: `${LIST_MAX_HEIGHT}px` }}
            onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
          >
            {filteredOptions.length > 0 ? (
              <div>
                {topSpacerHeight > 0 ? (
                  <div style={{ height: `${topSpacerHeight}px` }} aria-hidden="true" />
                ) : null}
                {visibleOptions.map((entry, localIndex) => {
                  const index = startIndex + localIndex;
                  const title = getOptionTitle(entry, index);
                const optionValue = getOptionValue(entry, index);
                const favoriteLookupValue =
                  typeof optionValue === "string"
                    ? optionValue.trim()
                    : String(optionValue || "").trim();
                const isFavorited = favoriteSet.has(favoriteLookupValue);
                const isSelectedOption = isOptionSelected(optionValue);
                return (
                  <div
                    key={`quick-${optionValue}-${index}`}
                    className="flex items-center gap-1 px-1 py-0.5"
                    style={{ minHeight: `${OPTION_ROW_HEIGHT}px` }}
                  >
                    <button
                      type="button"
                      id={`${resultsListId}-option-${index}`}
                      data-option-index={index}
                      role="option"
                      aria-selected={isSelectedOption}
                      aria-posinset={index + 1}
                      aria-setsize={filteredOptions.length}
                      onClick={() => handleQuickSelect(optionValue, title)}
                      onFocus={() => {
                        setActiveOptionIndex(index);
                        setLiveMessage(
                          `${title}${isSelectedOption ? ", selected" : ""}, option ${index + 1} of ${filteredOptions.length}`
                        );
                      }}
                      onMouseEnter={() => setActiveOptionIndex(index)}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          moveOptionFocus(1);
                          return;
                        }
                        if (event.key === "ArrowUp") {
                          event.preventDefault();
                          moveOptionFocus(-1);
                          return;
                        }
                        if (event.key === "Home") {
                          event.preventDefault();
                          focusOptionAtIndex(0);
                          return;
                        }
                        if (event.key === "End") {
                          event.preventDefault();
                          focusOptionAtIndex(filteredOptions.length - 1);
                          return;
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          closePicker({ clearSearch: true });
                          if (searchInputRef.current) {
                            searchInputRef.current.focus();
                          }
                        }
                      }}
                      className="min-w-0 flex-1 rounded px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {title}
                      {isSelectedOption ? <span className="sr-only">, selected</span> : null}
                    </button>
                    <button
                      type="button"
                      aria-label={
                        isFavorited
                          ? `Remove ${title} from favorites`
                          : `Add ${title} to favorites`
                      }
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        if (!onToggleFavorite) return;
                        onToggleFavorite(entry);
                      }}
                      disabled={disabled || isUpdatingFavorites || !onToggleFavorite}
                      className={`inline-flex shrink-0 items-center justify-center rounded text-[11px] font-semibold transition-colors ${
                        isFavorited
                          ? "h-6 w-6 bg-[#783bff] text-white"
                          : "min-w-[62px] bg-blue-700 px-2 py-1 text-white hover:bg-blue-800"
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      {isFavorited ? (
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M3.5 8.5L6.5 11.5L12.5 4.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        "Favorite"
                      )}
                    </button>
                  </div>
                );
                })}
                {bottomSpacerHeight > 0 ? (
                  <div style={{ height: `${bottomSpacerHeight}px` }} aria-hidden="true" />
                ) : null}
              </div>
            ) : (
              <div className="px-2 py-1 text-xs text-gray-500">
                {viewMode === "favorites"
                  ? "No favorite note titles found"
                  : "No matching note titles"}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1 text-[11px] text-knowtex-grey">
            <span>
              Showing {filteredOptions.length} of {optionsForCurrentView.length}
              {viewMode === "favorites" ? ` favorites` : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => closePicker({ clearSearch: true })}
                className="rounded px-1.5 py-1 text-[11px] text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                Close
              </button>
              <div className="inline-flex overflow-hidden rounded border border-gray-200">
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setViewMode("all")}
                  aria-pressed={viewMode === "all"}
                  className={`px-2 py-1 text-[11px] font-semibold ${
                    viewMode === "all"
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  All ({options.length})
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setViewMode("favorites")}
                  aria-pressed={viewMode === "favorites"}
                  className={`border-l border-gray-200 px-2 py-1 text-[11px] font-semibold ${
                    viewMode === "favorites"
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Favorites ({availableFavoriteCount})
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NoteTitleDropdown;